<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useReleaseNotesStore } from 'dashboard/stores/releaseNotes';
import {
  formatNoteDate,
  localizeNoteText,
  notesUpTo,
  unseenNotes,
} from 'dashboard/releaseNotes/helpers';

// MA: novidades da versão. Abre sozinho na primeira vez que o usuário entra numa
// versão com notas novas; depois só pelo menu do perfil ou pela versão em
// Configurações → Conta.
const LAST_SEEN_KEY = 'release_notes_last_seen_version';
const TYPE_CLASSES = {
  new: 'bg-n-teal-3 text-n-teal-11',
  improvement: 'bg-n-blue-3 text-n-blue-11',
  fix: 'bg-n-amber-3 text-n-amber-11',
};

const { t, locale } = useI18n();
const globalConfig = useMapGetter('globalConfig/get');
const { uiSettings, updateUISettings } = useUISettings();
const releaseNotesStore = useReleaseNotesStore();

const dialogRef = ref(null);
const isHistory = ref(false);
const notes = ref([]);

const currentVersion = computed(() => globalConfig.value.maVersion);

const formatDate = date => formatNoteDate(date, locale.value);
const localize = text => localizeNoteText(text, locale.value);

const typeLabels = computed(() => ({
  new: t('RELEASE_NOTES.TYPES.NEW'),
  improvement: t('RELEASE_NOTES.TYPES.IMPROVEMENT'),
  fix: t('RELEASE_NOTES.TYPES.FIX'),
}));

const open = (list, history) => {
  if (!list.length) return;

  notes.value = list;
  isHistory.value = history;
  dialogRef.value?.open();
};

const onClose = () => {
  if (!isHistory.value) {
    updateUISettings({ [LAST_SEEN_KEY]: currentVersion.value });
  }
  releaseNotesStore.closeHistory();
};

onMounted(() => {
  open(
    unseenNotes(currentVersion.value, uiSettings.value[LAST_SEEN_KEY]),
    false
  );
});

watch(
  () => releaseNotesStore.isHistoryOpen,
  isOpen => {
    if (isOpen) open(notesUpTo(currentVersion.value), true);
  }
);
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    overflow-y-auto
    :title="t('RELEASE_NOTES.TITLE')"
    :description="isHistory ? '' : t('RELEASE_NOTES.DESCRIPTION')"
    :show-cancel-button="false"
    :confirm-button-label="
      isHistory ? t('RELEASE_NOTES.CLOSE') : t('RELEASE_NOTES.GOT_IT')
    "
    @confirm="dialogRef.close()"
    @close="onClose"
  >
    <div class="flex flex-col gap-6 max-h-[60vh] overflow-y-auto pe-1">
      <section
        v-for="note in notes"
        :key="note.version"
        class="flex flex-col gap-3"
      >
        <div class="flex items-baseline gap-2">
          <h4 class="text-sm font-medium text-n-slate-12">
            {{ t('RELEASE_NOTES.VERSION', { version: note.version }) }}
          </h4>
          <span class="text-xs text-n-slate-10">{{
            formatDate(note.date)
          }}</span>
        </div>
        <ul class="flex flex-col gap-3">
          <li
            v-for="highlight in note.highlights"
            :key="highlight.title.en"
            class="flex flex-col gap-1"
          >
            <div class="flex items-center gap-2">
              <span
                class="px-1.5 py-0.5 text-xs font-medium rounded-md"
                :class="TYPE_CLASSES[highlight.type]"
              >
                {{ typeLabels[highlight.type] }}
              </span>
              <span class="text-sm font-medium text-n-slate-12">
                {{ localize(highlight.title) }}
              </span>
            </div>
            <p class="mb-0 text-sm text-n-slate-11">
              {{ localize(highlight.description) }}
            </p>
          </li>
        </ul>
      </section>
    </div>
  </Dialog>
</template>
